import React, { useState, useEffect } from 'react'
import { apiClient } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/helpers'
import {
  Calendar, UserX, MapPin, Clock, Check, X, Edit2, Plus,
  CalendarRange, Filter, Search, ChevronDown, Eye, EyeOff,
  Save, Trash2, CalendarX, ChevronLeft, ChevronRight
} from 'lucide-react'
import { format, parseISO, differenceInDays, eachDayOfInterval, isWeekend, addDays, isPast, isFuture, isToday, isWithinInterval, startOfMonth, endOfMonth, subDays } from 'date-fns'
import { formatTimeString } from '@/utils/timezoneUtils'

interface AvailabilityOverride {
  id: string
  user_id: string
  override_date: string
  override_type: 'at_julies' | 'work' | 'school' | 'friend' | 'event' | 'other'
  reason?: string
  location?: string
  all_day: boolean
  start_time?: string
  end_time?: string
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  days_notice?: number
}

interface Child {
  id: string
  username: string
  display_name: string
}

interface ParentChildAvailabilityEnhancedProps {
  onUpdate?: () => void
}

const overrideTypeLabels = {
  at_toms: "At Home (Parent 1)",
  at_julies: "At Home (Parent 2)",
  work: 'Work',
  school: 'School',
  friend: "Friend's House",
  event: 'Event/Activity',
  other: 'Other'
}

const overrideTypeColors = {
  at_toms: 'bg-blue-100 text-blue-800',
  at_julies: 'bg-purple-100 text-purple-800',
  work: 'bg-blue-100 text-blue-800',
  school: 'bg-orange-100 text-orange-800',
  friend: 'bg-green-100 text-green-800',
  event: 'bg-yellow-100 text-yellow-800',
  other: 'bg-gray-100 text-gray-800'
}

type ViewFilter = 'all' | 'upcoming' | 'past' | 'thisMonth' | 'nextMonth' | 'pending' | 'approved'
type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected'

const ITEMS_PER_PAGE = 10

export function ParentChildAvailabilityEnhanced({ onUpdate }: ParentChildAvailabilityEnhancedProps) {
  const [overrides, setOverrides] = useState<AvailabilityOverride[]>([])
  const [filteredOverrides, setFilteredOverrides] = useState<AvailabilityOverride[]>([])
  const [children, setChildren] = useState<Child[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [editingOverride, setEditingOverride] = useState<AvailabilityOverride | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const { user } = useAuth()

  // Filters
  const [viewFilter, setViewFilter] = useState<ViewFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [childFilter, setChildFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [dateRangeStart, setDateRangeStart] = useState('')
  const [dateRangeEnd, setDateRangeEnd] = useState('')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [showAllItems, setShowAllItems] = useState(false)
  
  const [newOverride, setNewOverride] = useState({
    child_id: '',
    override_date: '',
    end_date: '', 
    override_type: 'other' as const,
    reason: '',
    location: '',
    all_day: true,
    start_time: '',
    end_time: '',
    use_date_range: false,
    skip_weekends: false
  })

  useEffect(() => {
    if (user?.is_parent) {
      loadData()
    }
  }, [user])

  useEffect(() => {
    applyFilters()
    setCurrentPage(1) // Reset to first page when filters change
  }, [overrides, viewFilter, statusFilter, childFilter, searchQuery, dateRangeStart, dateRangeEnd])

  // Calculate pagination
  const totalItems = filteredOverrides.length
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedOverrides = showAllItems ? filteredOverrides : filteredOverrides.slice(startIndex, endIndex)

  const loadData = async () => {
    setIsLoading(true)
    try {
      // Load all children (including inactive, for lookups in availability overrides)
      const usersResponse = await apiClient.getUsers()
      // Defensive check: Ensure usersResponse is an array
      const usersArray = Array.isArray(usersResponse) ? usersResponse :
                         (usersResponse?.users && Array.isArray(usersResponse.users)) ? usersResponse.users : []
      const childUsers = usersArray.filter((u: any) => !u.is_parent)
      setChildren(childUsers)

      // Load availability overrides using modern endpoint
      try {
        const overridesResponse = await apiClient.get('/api/availability/overrides')
        // Defensive check: Ensure overridesResponse is an array
        const overridesArray = Array.isArray(overridesResponse) ? overridesResponse :
                               (overridesResponse?.overrides && Array.isArray(overridesResponse.overrides)) ? overridesResponse.overrides : []
        setOverrides(overridesArray)
      } catch (error) {
        console.warn('Availability overrides endpoint not available:', error)
        setOverrides([])
      }
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load availability data')
    }
    setIsLoading(false)
  }

  const applyFilters = () => {
    let filtered = [...overrides]
    const today = new Date()

    // Filter out items with missing override_date
    filtered = filtered.filter(o => o.override_date)

    // View filter
    switch (viewFilter) {
      case 'upcoming':
        filtered = filtered.filter(o => isFuture(parseISO(o.override_date)) || isToday(parseISO(o.override_date)))
        break
      case 'past':
        filtered = filtered.filter(o => isPast(parseISO(o.override_date)) && !isToday(parseISO(o.override_date)))
        break
      case 'thisMonth':
        const thisMonthStart = startOfMonth(today)
        const thisMonthEnd = endOfMonth(today)
        filtered = filtered.filter(o =>
          isWithinInterval(parseISO(o.override_date), { start: thisMonthStart, end: thisMonthEnd })
        )
        break
      case 'nextMonth':
        const nextMonth = addDays(today, 30)
        const nextMonthStart = startOfMonth(nextMonth)
        const nextMonthEnd = endOfMonth(nextMonth)
        filtered = filtered.filter(o =>
          isWithinInterval(parseISO(o.override_date), { start: nextMonthStart, end: nextMonthEnd })
        )
        break
      case 'pending':
        filtered = filtered.filter(o => o.status === 'pending')
        break
      case 'approved':
        filtered = filtered.filter(o => o.status === 'approved')
        break
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === statusFilter)
    }

    // Child filter
    if (childFilter !== 'all') {
      filtered = filtered.filter(o => o.user_id === childFilter)
    }

    // Date range filter
    if (dateRangeStart && dateRangeEnd) {
      const start = parseISO(dateRangeStart)
      const end = parseISO(dateRangeEnd)
      filtered = filtered.filter(o => 
        isWithinInterval(parseISO(o.override_date), { start, end })
      )
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(o => 
        o.reason?.toLowerCase().includes(query) ||
        o.location?.toLowerCase().includes(query) ||
        getChildName(o.user_id).toLowerCase().includes(query)
      )
    }

    // Sort by date - most recent first for past events, earliest first for upcoming
    filtered.sort((a, b) => {
      const dateA = new Date(a.override_date + 'T12:00:00')
      const dateB = new Date(b.override_date + 'T12:00:00')
      
      // For upcoming and current view, show nearest dates first
      if (viewFilter === 'upcoming' || viewFilter === 'all') {
        // Separate past and future
        const isPastA = isPast(dateA) && !isToday(dateA)
        const isPastB = isPast(dateB) && !isToday(dateB)
        
        if (isPastA && !isPastB) return 1  // Past items go to the end
        if (!isPastA && isPastB) return -1 // Future items go to the beginning
        
        // Both are same type (past or future), sort by date
        if (isPastA && isPastB) {
          return dateB.getTime() - dateA.getTime() // Recent past first
        } else {
          return dateA.getTime() - dateB.getTime() // Nearest future first
        }
      }
      
      // For past view, show most recent first
      if (viewFilter === 'past') {
        return dateB.getTime() - dateA.getTime()
      }
      
      // Default: ascending order (earliest first)
      return dateA.getTime() - dateB.getTime()
    })

    setFilteredOverrides(filtered)
  }

  const isToday = (date: Date) => {
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  const handleUpdateOverride = async () => {
    if (!editingOverride) return

    try {
      await apiClient.put(`/api/availability/overrides/${editingOverride.id}`, {
        user_id: editingOverride.user_id,
        override_date: editingOverride.override_date,
        override_type: editingOverride.override_type || 'other',
        reason: editingOverride.reason || 'Unavailable',
        location: editingOverride.location,
        all_day: editingOverride.all_day,
        start_time: editingOverride.all_day ? null : editingOverride.start_time,
        end_time: editingOverride.all_day ? null : editingOverride.end_time,
        status: editingOverride.status
      })

      toast.success('Override updated')
      setEditingOverride(null)
      loadData()

      if (onUpdate) {
        onUpdate()
      }
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to update override'))
    }
  }

  const handleAddOverride = async () => {
    if (!newOverride.child_id || !newOverride.override_date) {
      toast.error('Please select a child and date')
      return
    }

    if (newOverride.use_date_range && !newOverride.end_date) {
      toast.error('Please select an end date for the range')
      return
    }

    try {
      const token = localStorage.getItem('auth_token')
      
      let datesToCreate: Date[] = []
      
      if (newOverride.use_date_range) {
        const startDate = parseISO(newOverride.override_date)
        const endDate = parseISO(newOverride.end_date)
        
        if (endDate < startDate) {
          toast.error('End date must be after start date')
          return
        }
        
        const allDates = eachDayOfInterval({ start: startDate, end: endDate })
        
        datesToCreate = newOverride.skip_weekends 
          ? allDates.filter(date => !isWeekend(date))
          : allDates
          
        if (datesToCreate.length === 0) {
          toast.error('No valid dates in the selected range')
          return
        }
        
        if (datesToCreate.length > 30) {
          if (!confirm(`This will create ${datesToCreate.length} override entries. Continue?`)) {
            return
          }
        }
      } else {
        datesToCreate = [parseISO(newOverride.override_date)]
      }
      
      let successCount = 0
      let errorCount = 0
      
      for (const date of datesToCreate) {
        try {
          await apiClient.post('/api/availability/overrides', {
            user_id: newOverride.child_id,
            override_date: format(date, 'yyyy-MM-dd'),
            override_type: newOverride.override_type || 'other',
            reason: newOverride.reason || 'Unavailable',
            location: newOverride.location,
            all_day: newOverride.all_day,
            start_time: newOverride.all_day ? null : newOverride.start_time,
            end_time: newOverride.all_day ? null : newOverride.end_time
          })
          successCount++
        } catch (error) {
          console.error('Failed to create override:', error)
          errorCount++
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully added ${successCount} override${successCount !== 1 ? 's' : ''}`)
      }
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} override${errorCount !== 1 ? 's' : ''}`)
      }
      
      setShowAddForm(false)
      setNewOverride({
        child_id: '',
        override_date: '',
        end_date: '',
        override_type: 'other',
        reason: '',
        location: '',
        all_day: true,
        start_time: '',
        end_time: '',
        use_date_range: false,
        skip_weekends: false
      })
      
      loadData()
      if (onUpdate) {
        onUpdate()
      }
    } catch (error) {
      toast.error('Failed to add override')
    }
  }

  const handleDeleteOverride = async (id: string) => {
    if (!confirm('Are you sure you want to delete this availability override?')) {
      return
    }

    try {
      await apiClient.delete(`/api/availability/overrides/${id}`)

      toast.success('Override deleted')
      loadData()

      if (onUpdate) {
        onUpdate()
      }
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to delete override'))
    }
  }

  const getChildName = (userId: string) => {
    const child = children.find(c => c.id === userId)
    return child?.display_name || userId || 'Unknown'
  }

  const getDaysNotice = (overrideDate: string, createdAt: string) => {
    const created = parseISO(createdAt)
    const override = parseISO(overrideDate)
    return differenceInDays(override, created)
  }

  if (!user?.is_parent) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Only parents can manage child availability</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Informational banner explaining the feature */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-2">
          <div className="text-blue-600 mt-0.5">
            <CalendarX className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Child Availability Management</h3>
            <p className="text-sm text-blue-800">
              Track when your children will be away or unavailable for chores (trips, camps, activities, etc.).
              When you mark a child as unavailable, the system can help reassign their chores to other family members.
              Add overrides below or approve requests submitted by your kids.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Availability Overrides</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Override
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </h3>
          <button
            onClick={() => {
              setViewFilter('upcoming')
              setStatusFilter('all')
              setChildFilter('all')
              setSearchQuery('')
              setDateRangeStart('')
              setDateRangeEnd('')
            }}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Clear Filters
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* View Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">View</label>
            <select
              value={viewFilter}
              onChange={(e) => setViewFilter(e.target.value as ViewFilter)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Overrides</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
              <option value="thisMonth">This Month</option>
              <option value="nextMonth">Next Month</option>
              <option value="pending">Pending Only</option>
              <option value="approved">Approved Only</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Child Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Child</label>
            <select
              value={childFilter}
              onChange={(e) => setChildFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Children</option>
              {children.map(child => (
                <option key={child.id} value={child.id}>{child.display_name}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search reason/location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Date Range */}
          <div className="md:col-span-2 lg:col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={dateRangeStart}
                onChange={(e) => setDateRangeStart(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRangeEnd}
                onChange={(e) => setDateRangeEnd(e.target.value)}
                min={dateRangeStart}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {showAllItems ? (
              <>Showing all {filteredOverrides.length} of {overrides.length} overrides</>
            ) : (
              <>Showing {Math.min(startIndex + 1, totalItems)}-{Math.min(endIndex, totalItems)} of {filteredOverrides.length} filtered overrides ({overrides.length} total)</>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAllItems(!showAllItems)}
              className="px-3 py-1 text-sm rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors flex items-center gap-1"
            >
              {showAllItems ? (
                <>
                  <EyeOff className="h-4 w-4" />
                  Paginate
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" />
                  Show All
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Add Availability Override</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Child
              </label>
              <select
                value={newOverride.child_id}
                onChange={(e) => setNewOverride({ ...newOverride, child_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Select a child</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.display_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {newOverride.use_date_range ? 'Start Date' : 'Date'}
              </label>
              <input
                type="date"
                value={newOverride.override_date}
                onChange={(e) => setNewOverride({ ...newOverride, override_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {newOverride.use_date_range && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={newOverride.end_date}
                  onChange={(e) => setNewOverride({ ...newOverride, end_date: e.target.value })}
                  min={newOverride.override_date}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={newOverride.override_type}
                onChange={(e) => setNewOverride({ ...newOverride, override_type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {Object.entries(overrideTypeLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason/Event
              </label>
              <input
                type="text"
                value={newOverride.reason}
                onChange={(e) => setNewOverride({ ...newOverride, reason: e.target.value })}
                placeholder="e.g., Father's Day"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={newOverride.location}
                onChange={(e) => setNewOverride({ ...newOverride, location: e.target.value })}
                placeholder="e.g., other parent's house"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="md:col-span-2 space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newOverride.use_date_range}
                  onChange={(e) => setNewOverride({ ...newOverride, use_date_range: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Create for date range</span>
              </label>
              
              {newOverride.use_date_range && (
                <>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={newOverride.skip_weekends}
                      onChange={(e) => setNewOverride({ ...newOverride, skip_weekends: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Skip weekends</span>
                  </label>
                </>
              )}
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newOverride.all_day}
                  onChange={(e) => setNewOverride({ ...newOverride, all_day: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">All day</span>
              </label>
            </div>

            {!newOverride.all_day && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={newOverride.start_time}
                    onChange={(e) => setNewOverride({ ...newOverride, start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={newOverride.end_time}
                    onChange={(e) => setNewOverride({ ...newOverride, end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => {
                setShowAddForm(false)
                setNewOverride({
                  child_id: '',
                  override_date: '',
                  end_date: '',
                  override_type: 'other',
                  reason: '',
                  location: '',
                  all_day: true,
                  start_time: '',
                  end_time: '',
                  use_date_range: false,
                  skip_weekends: false
                })
              }}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddOverride}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Add Override
            </button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingOverride && (
        <div className="bg-yellow-50 rounded-lg shadow-md p-6 border-2 border-yellow-200">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Edit2 className="h-5 w-5" />
            Edit Override
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Child
              </label>
              <select
                value={editingOverride.user_id}
                onChange={(e) => setEditingOverride({ ...editingOverride, user_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.display_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={editingOverride.override_date}
                onChange={(e) => setEditingOverride({ ...editingOverride, override_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={editingOverride.override_type}
                onChange={(e) => setEditingOverride({ ...editingOverride, override_type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {Object.entries(overrideTypeLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={editingOverride.status}
                onChange={(e) => setEditingOverride({ ...editingOverride, status: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason/Event
              </label>
              <input
                type="text"
                value={editingOverride.reason || ''}
                onChange={(e) => setEditingOverride({ ...editingOverride, reason: e.target.value })}
                placeholder="e.g., Father's Day"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={editingOverride.location || ''}
                onChange={(e) => setEditingOverride({ ...editingOverride, location: e.target.value })}
                placeholder="e.g., other parent's house"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingOverride.all_day}
                  onChange={(e) => setEditingOverride({ ...editingOverride, all_day: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">All day</span>
              </label>
            </div>

            {!editingOverride.all_day && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={editingOverride.start_time || ''}
                    onChange={(e) => setEditingOverride({ ...editingOverride, start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={editingOverride.end_time || ''}
                    onChange={(e) => setEditingOverride({ ...editingOverride, end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setEditingOverride(null)}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateOverride}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Save Changes
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold">Current & Upcoming Overrides</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Child
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location/Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedOverrides.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No availability overrides found
                  </td>
                </tr>
              ) : (
                paginatedOverrides.map((override) => (
                  <tr key={override.id} className={editingOverride?.id === override.id ? 'bg-yellow-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getChildName(override.user_id)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                        {format(parseISO(override.override_date), 'MMM d, yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${overrideTypeColors[override.override_type]}`}>
                        {overrideTypeLabels[override.override_type]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {override.reason || '-'}
                        {override.location && (
                          <div className="text-xs text-gray-500 flex items-center mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            {override.location}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {override.all_day ? (
                          'All day'
                        ) : (
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1 text-gray-400" />
                            {formatTimeString(override.start_time)} - {formatTimeString(override.end_time)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        override.status === 'approved' ? 'bg-green-100 text-green-800' :
                        override.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {override.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingOverride(override)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteOverride(override.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {!showAllItems && totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}